<script lang="ts" setup>
import type { PropType} from 'vue';
import { computed, ref, watch } from 'vue';
import SearchBar from '@/components/common/forms/SearchBar.vue';
import { useI18n } from 'vue-i18n';
import type { TreeBase, TreeNode, TreeNodeSelectSettings} from '@/composables/useTreeNode';
import { useTreeNode } from '@/composables/useTreeNode';
import PopupMenuCheckbox from '@/components/common/popup/PopupMenuCheckbox.vue';
import Checkbox from '@/components/common/Checkbox.vue';
import PopupMenuItem from '@/components/common/popup/PopupMenuItem.vue';

const { t } = useI18n();

const emit = defineEmits<{
    change: [changedNodes: (TreeBase | null)[], newSelectedState: string[]];
}>();

const props = defineProps({
    selectedValues: {
        type: Array as PropType<string[]>,
        default: () => [],
    },
    options: {
        type: Array as PropType<TreeBase[]>,
        default: () => [],
    },
    selectOptionTitle: {
        type: String,
        default: undefined
    },
    allowDeselect: {
        type: Boolean,
        default: false
    },
    isCheckbox: {
        type: Boolean,
        default: false
    },
    settings: {
        type: Object as PropType<TreeNodeSelectSettings>,
        default: () => ({})
    },
    wide: {
        type: Boolean,
        default: false
    }
});

const parentIdRef = ref<HTMLInputElement[] | null>();

const { nodes, createTree, searchString, isNodeVisible, noNodesVisible } = useTreeNode(props.settings);

const showOnlyId = ref('');

const selectOptionTranslation = computed(() => (props.selectOptionTitle ?? t('components.treeNodeSelect.selectOption')));
const showDeselectOption = computed(() => props.settings.showDeselectOption === true && searchString.value === '');

function searchTabbed(): void {
    if (parentIdRef.value == null) return;
    parentIdRef.value[0].checked = true;
}

function getRecursiveChildren(node: TreeNode): TreeNode[] {
    let children: TreeNode[] = [];
    if (node.children.length > 0) {
        node.children.forEach(child => {
            children.push(child);
            children = [...children, ...getRecursiveChildren(child)];
        });
    }
    return children;
}

function selectNode(node: TreeNode | null, selectChildren?: boolean): void {
    if (node == null) {
        emit('change', [null], []);
        return;
    }
    const selectState = !isSelectedNode(node.id);
    const nodes: TreeNode[] = [node];
    if (selectChildren === true && node != null) {
        if (node.children.length > 0) {
            nodes.push(...getRecursiveChildren(node));
        }
    }

    let newSelectedState = [];
    const changedNodeIds = nodes.map(n => n.id);
    if (selectState === true) {
        newSelectedState = [...props.selectedValues, ...changedNodeIds];
    } else {
        newSelectedState = props.selectedValues.filter(value => !changedNodeIds.includes(value));
    }
    emit('change', nodes, newSelectedState);
}

function isSelectedNode(nodeId: string | null): boolean {
    if (nodeId == null) {
        return props.selectedValues.length === 0;
    }
    return props.selectedValues.includes(nodeId);
}

watch(() => props.options, () => {
    createTree(props.options, props.settings);
}, { immediate: true });

</script>

<template>
    <div class="sy-popup-large" :class="{ 'extra-wide': wide }">
        <div class="padding-5 sy-border-bottom" @click.stop>
            <SearchBar
                id="parent-node"
                ref="input"
                v-model="searchString"
                :autofocus="true"
                @tab="searchTabbed()"
            />
        </div>
        <div class="sy-node-list">
            <PopupMenuItem v-if="noNodesVisible" :text="$t('components.treeNodeSelect.noMatches')" class="no-matches" />
            <PopupMenuCheckbox
                v-if="searchString === '' && allowDeselect"
                class="sy-node"
                :data-level="0"
                :title="selectOptionTranslation"
                :checked="isSelectedNode(null)"
            />
            <PopupMenuItem
                v-if="showDeselectOption"
                class="sy-node sy-type-italic sy-type-gray"
                :data-level="0"
                :data-id="null"
                :text="settings.deselectOptionTitle ?? ''"
                @click="selectNode(null, settings.parentSelectsChildren)"
            />
            <template v-for="(node) of nodes" :key="node.id">
                <template v-if="isCheckbox">
                    <div
                        v-if="node.children.length > 0"
                        v-show="isNodeVisible(node)"
                        :data-level="node.level"
                        :data-id="node.id"
                        class="sy-node sy-popup-menu-button flex-container align-justify"
                        @mouseenter="showOnlyId = node.id"
                        @mouseleave="showOnlyId = ''"
                    >
                        <Checkbox
                            v-if="isCheckbox"
                            :checked="isSelectedNode(node.id)"
                            size="small"
                            @change="selectNode(node, settings.parentSelectsChildren)"
                            @keydown.enter="selectNode(node, settings.parentSelectsChildren)"
                        >
                            <span class="cell auto sy-truncate">{{ node.name }}</span>
                        </Checkbox>
                        <button v-show="showOnlyId === node.id" class="sy-border-none padding-0 sy-type-standard sy-type-blue" @click="selectNode(node, false)">
                            {{ $t('components.treeNodeSelect.onlyParent') }}
                        </button>
                    </div>
                    <PopupMenuCheckbox
                        v-else
                        v-show="isNodeVisible(node)"
                        ref="parentIdRef"
                        class="sy-node"
                        :data-level="node.level"
                        :data-id="node.id"
                        :title="node.name"
                        :checked="isSelectedNode(node.id)"
                        @change="selectNode(node, settings.parentSelectsChildren)"
                    />
                </template>
                <PopupMenuItem
                    v-else
                    v-show="isNodeVisible(node)"
                    ref="parentIdRef"
                    class="sy-node"
                    :data-level="node.level"
                    :data-id="node.id"
                    :text="node.name"
                    :selected="isSelectedNode(node.id)"
                    @click="selectNode(node, settings.parentSelectsChildren)"
                />
            </template>
        </div>
    </div>
</template>

<style scoped lang="scss">
.sy-popup-large.extra-wide {
    min-width: 400px;
    max-width: 400px;
}

// Prevents the no matches button from looking clickable
:deep(.no-matches) {
    pointer-events: none;
}
</style>
